/* eslint-disable prefer-const */
/* eslint-disable functional/no-let */
import { Icon as DsIcon } from "@equipmentshare/ds-icon";
import {
  Alert,
  Button,
  Col,
  Drawer,
  Form,
  List,
  Modal,
  notification,
  Row,
  Tooltip,
} from "antd";
import React, { useEffect, useMemo, useState } from "react";
import isEqual from "lodash/isEqual";
import styled from "styled-components";

import { Company } from "~/Company";
import { DVIR } from "~/DVIR";
import * as ErrorMessage from "~/ErrorMessage";
import { HoursOfService } from "~/HoursOfService";
import * as Icon from "~/Icon";
import * as Layout from "~/Layout";
import * as Loading from "~/Loading";
import { Remote } from "~/Remote";
import * as Typography from "~/Typography";

import { Requirements } from "./DVIR/Requirements";
import { Location } from "./Company/Location";
import { Graph } from "./Graph";
import { heapEvent } from "@equipmentshare/heaper";
import { useFlags } from "launchdarkly-react-client-sdk";

// TODO: Move to `~/Company.Settings`
export type Settings = {
  readonly isVisible: boolean;
  readonly shouldWarn: boolean;
  readonly show: () => void;
  readonly hide: (forceHide?: boolean) => void;
  readonly toggleWarning: (warn: boolean) => void;
};

const settingsConfirmChangedKeyDescriptions = (data = "") => ({
  location: <>Changed Company Address.</>,
  hoursOfServiceType: <>Changed Hours of Service Type.</>,
  vehicleDefectTagsAdded: <>Added {data} new Vehicle Defect Tags.</>,
  trailerDefectTagsAdded: <>Added {data} new Trailer Defect Tags.</>,
  vehicleDefectTagsRemoved: <>Removed {data} new Vehicle Defect Tags.</>,
  trailerDefectTagsRemoved: <>Removed {data} new Trailer Defect Tags.</>,
  requireNotes: (
    <>
      Require drivers to include a note on <b>all</b> DVIRs.
    </>
  ),
  requirePhotos: (
    <>
      Require drivers to include a photo on <b>all</b> DVIRs.
    </>
  ),
  requireDefectListView: (
    <>
      Require drivers to view the list of defects on <b>all</b> DVIRs.
    </>
  ),
  enablePairingSuggestions: (
    <>
      Allow users to receive pairing suggestion push notifications from facial recognition.
    </>
  ),
});

export type SettingKeys = keyof ReturnType<
  typeof settingsConfirmChangedKeyDescriptions
>;

export function Settings({ isVisible, hide }: Settings) {
  const [cancelAndClose, setCancelAndClose] = React.useState(false);
  const { company } = Company.use({
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const onClose = () => {
    setTimeout(() => {
      hide();
    });
  };

  useEffect(() => {
    if (cancelAndClose) {
      setCancelAndClose(false);
    }
  }, [cancelAndClose, isVisible]);

  return (
    <DrawerContainer>
      <Settings.Update
        isVisible={isVisible}
        company={company}
        onClose={onClose}
        requestCancel={cancelAndClose}
      />
    </DrawerContainer>
  );
}

export const DrawerContainer = styled.div`
  &.drawer-cancel-modal-wrapper.ant-modal {
    display: fixed;
    top: 40px;
    left: 0;
    margin: 10px 0;
  }
`;

export namespace Settings {
  export const use = (): Settings => {
    const [settingsControl, updateSettingsControl] = React.useState({
      shouldWarn: false,
      isVisible: false,
    });
    return {
      isVisible: settingsControl.isVisible,
      show: () =>
        updateSettingsControl({ ...settingsControl, isVisible: true }),
      hide: (forceHide = false) => {
        if (forceHide || !settingsControl.shouldWarn) {
          updateSettingsControl({
            ...settingsControl,
            isVisible: false,
            shouldWarn: false,
          });
        }
      },
      shouldWarn: settingsControl.shouldWarn,
      toggleWarning: (warn) =>
        updateSettingsControl({ ...settingsControl, shouldWarn: warn }),
    };
  };

  export function Update({
    company,
    onClose,
    requestCancel,
    isVisible,
  }: Company & {
    readonly onClose: () => void;
    readonly requestCancel: boolean;
    readonly isVisible: boolean;
  }) {
    const { enableElogsFacialRecognitionSuggestions } = useFlags()

    const companyInvalidate = Company.Details.Invalidate.use();
    const [refresh, setRefresh] = React.useState(false);

    const [
      driveSettings,
      setDriveSettings,
    ] = useState<Company.Settings.DriveAndTrackSettings | null>(null);

    const companyUpdate = Company.Details.Update.use();

    const details = pipe(
      company,
      Remote.getOrElseW(() => undefined)
    );

    const [currentCompanyDetails, setCurrentCompanyDetails] = React.useState<
      typeof details | null
    >(null);

    const [isConfirmation, setConfirmation] = React.useState(false);

    const location = Company.Locations.SelectOne.use({ ...details });
    const hoursOfServiceType = HoursOfService.Type.Select.use(
      details?.hoursOfService
    );

    if (!isEqual(details, currentCompanyDetails) || refresh) {
      Company.Locations.SelectOne.initialize(location)({ ...details });
      HoursOfService.Type.Select.initialize(hoursOfServiceType)(
        details?.hoursOfService
      );
      setCurrentCompanyDetails(details);
    }

    const {
      component: defectVehicleEdit,
      save: saveVehicleDefectTypes,
      newTypes: vehicleDefectTypeAddedCount,
      removedTypes: vehicleDefectTypeRemovedCount,
      changed: areVehicleEditsAvailable,
      refetch: refetchVehicleDefectTypes,
    } = DVIR.DefectType.Editor.use("vehicle", refresh);

    const {
      component: defectTrailerEdit,
      save: saveTrailerDefectTypes,
      newTypes: trailerDefectTypeAddedCount,
      removedTypes: trailerDefectTypeRemovedCount,
      changed: areTrailerEditsAvailable,
      refetch: refetchTrailerDefectTypes,
    } = DVIR.DefectType.Editor.use("trailer", refresh);

    const {
      settings: currentDriveSettings,
      loading: driveSettingsLoading,
      refetch: driveSettingsRefetch,
      apolloError: driveQueryApolloError,
      graphError: driveQueryGraphError,
    } = Company.Settings.Query.use();

    const changedSettings = useMemo<readonly SettingKeys[]>(() => {
      let settings: readonly SettingKeys[] = [];
      if (
        location.selected?.location.id !== currentCompanyDetails?.location?.id
      )
        settings = [...settings, "location"];
      if (
        hoursOfServiceType.selected?.type.id !==
        currentCompanyDetails?.hoursOfService?.type?.id
      )
        settings = [...settings, "hoursOfServiceType"];
      if (vehicleDefectTypeAddedCount > 0) {
        settings = [...settings, "vehicleDefectTagsAdded"];
      }
      if (trailerDefectTypeAddedCount > 0) {
        settings = [...settings, "trailerDefectTagsAdded"];
      }
      if (vehicleDefectTypeRemovedCount > 0) {
        settings = [...settings, "vehicleDefectTagsRemoved"];
      }
      if (trailerDefectTypeRemovedCount > 0) {
        settings = [...settings, "trailerDefectTagsRemoved"];
      }
      if (
        driveSettings?.requireNotesForAllDVIRs !==
        currentDriveSettings?.requireNotesForAllDVIRs
      )
        settings = [...settings, "requireNotes"];
      if (
        driveSettings?.requireImagesForAllDVIRs !==
        currentDriveSettings?.requireImagesForAllDVIRs
      )
        settings = [...settings, "requirePhotos"];
      if (
        driveSettings?.requireDriverToViewAndAcceptCompanyDefectList !==
        currentDriveSettings?.requireDriverToViewAndAcceptCompanyDefectList
      )
        settings = [...settings, "requireDefectListView"];
      if (
        driveSettings?.enableFacialRecognitionPairingSuggestions !==
        currentDriveSettings?.enableFacialRecognitionPairingSuggestions
      )
        settings = [...settings, "enablePairingSuggestions"];

      return settings;
    }, [
      location.selected,
      hoursOfServiceType.selected,
      areVehicleEditsAvailable(),
      areTrailerEditsAvailable(),
      driveSettings,
      trailerDefectTypeAddedCount,
      trailerDefectTypeRemovedCount,
      vehicleDefectTypeAddedCount,
      vehicleDefectTypeRemovedCount,
    ]);

    if (
      !driveSettingsLoading &&
      currentDriveSettings &&
      (!driveSettings || refresh)
    ) {
      setDriveSettings(currentDriveSettings);
    }

    const mutationArguments = useMemo(
      () =>
        ({
          requireNotes: driveSettings?.requireNotesForAllDVIRs ?? false,
          requireImages: driveSettings?.requireImagesForAllDVIRs ?? false,
          requireDefectListReview:
            driveSettings?.requireDriverToViewAndAcceptCompanyDefectList ??
            false,
          enablePairingSuggestions:
            driveSettings?.enableFacialRecognitionPairingSuggestions ??
            false,
        } as const),
      [driveSettings, refresh]
    );

    const [
      mutateSettings,
      { error: driveMutationError },
    ] = Company.Settings.Update.use(mutationArguments);

    const saveDriveSettings = useMemo(() => {
      return driveSettingsLoading ||
        driveQueryApolloError ||
        driveQueryGraphError
        ? () => {}
        : mutateSettings;
    }, [
      driveSettingsLoading,
      driveQueryApolloError,
      driveQueryGraphError,
      refresh,
    ]);

    const valueIfSettingChanged = <T,>(
      settingKey: SettingKeys,
      value: T
    ): T | undefined => {
      return changedSettings.includes(settingKey) ? value : undefined;
    };

    const sendHeaperSaveEvent = () => {
      const extraProperties = {
        company_location: valueIfSettingChanged(
          "location",
          location.selected === undefined
            ? "Unknown"
            : Location.toString({ location: location.selected.location })
        ),
        hours_of_service_type: valueIfSettingChanged(
          "hoursOfServiceType",
          hoursOfServiceType.selected?.type.name
        ),
        vehicle_defect_tags_added: valueIfSettingChanged(
          "vehicleDefectTagsAdded",
          vehicleDefectTypeAddedCount
        ),
        vehicle_defect_tags_removed: valueIfSettingChanged(
          "vehicleDefectTagsRemoved",
          vehicleDefectTypeRemovedCount
        ),
        trailer_defect_tags_added: valueIfSettingChanged(
          "trailerDefectTagsAdded",
          trailerDefectTypeAddedCount
        ),
        trailer_defect_tags_removed: valueIfSettingChanged(
          "trailerDefectTagsRemoved",
          trailerDefectTypeRemovedCount
        ),
        require_defect_list_view: valueIfSettingChanged(
          "requireDefectListView",
          driveSettings?.requireDriverToViewAndAcceptCompanyDefectList
        ),
        require_notes: valueIfSettingChanged(
          "requireNotes",
          driveSettings?.requireNotesForAllDVIRs
        ),
        require_photos: valueIfSettingChanged(
          "requirePhotos",
          driveSettings?.requireImagesForAllDVIRs
        ),
        enable_pairing_suggestions: valueIfSettingChanged(
          "enablePairingSuggestions",
          driveSettings?.enableFacialRecognitionPairingSuggestions
        ),
      };
      heapEvent(
        "E-Logs - Settings - Click - Save settings changes",
        extraProperties
      );
    };

    useEffect(() => {
      if (driveQueryGraphError)
        Graph.Errors.displayMessage(driveQueryGraphError);
    }, [driveQueryGraphError]);

    const isSaveAllowed =
      areVehicleEditsAvailable() ||
      areTrailerEditsAvailable() ||
      hoursOfServiceType?.selected?.type.id !==
        currentCompanyDetails?.hoursOfService?.type?.id ||
      location.selected?.location.id !== currentCompanyDetails?.location?.id ||
      !isEqual(driveSettings, currentDriveSettings);

    const onSave = () => {
      if (!isConfirmation) {
        setConfirmation(true);
        return;
      }
      if (isSaveAllowed) {
        companyUpdate.request({
          company: {
            ...company,
            ...Remote.success({
              ...location.selected,
              ...(hoursOfServiceType.selected && {
                hoursOfService: hoursOfServiceType.selected,
              }),
            }),
          },
        });
        sendHeaperSaveEvent();
        saveVehicleDefectTypes();
        saveTrailerDefectTypes();
        void saveDriveSettings();
        setTimeout(() => {
          void refetchVehicleDefectTypes();
          void refetchTrailerDefectTypes();
          void driveSettingsRefetch();
          setTimeout(() => {
            onClose();
            setConfirmation(false);
            notification.open({
              message: "Settings Changes Saved!",
              description:
                "Your changes to E-Logs Company Settings have been successful",
              duration: 5,
              placement: "bottomRight",
              style: {
                borderTop: "2px solid #00CB86",
              },
            });
          }, 100);
        });
      }
    };

    const cancel = () => {
      if (isSaveAllowed) {
        CancelWarn.warnOnCancel((shouldSave) => {
          if (shouldSave) {
            onSave();
          } else {
            setConfirmation(false);
            notification.open({
              message: "Settings Changes Cancelled.",
              description:
                "Your changes to E-Logs Company Settings have been cancelled. No changes were saved.",
              duration: 5,
              placement: "bottomRight",
            });
            heapEvent(
              "E-Logs - Settings - Click - Cancel settings changes",
              {}
            );
            onClose();
            if (!refresh) {
              setRefresh(true);
            }
          }
        });
      } else {
        onClose();
        setConfirmation(false);
        if (!refresh && !requestCancel) {
          setRefresh(true);
        }
      }
    };

    if (requestCancel && !refresh) {
      cancel();
    }

    if (refresh) {
      void refetchVehicleDefectTypes();
      void refetchTrailerDefectTypes();
      void driveSettingsRefetch();
      void companyInvalidate();
      setRefresh(false);
    }

    return (
      <Drawer
        title={<DrawerHeader />}
        placement="left"
        width={460}
        push={false}
        visible={isVisible}
        onClose={cancel}
        footer={
          <Settings.Actions>
            <Button onClick={cancel}>Cancel</Button>
            <Button
              type="primary"
              disabled={
                Remote.isLoading(companyUpdate.response) || !isSaveAllowed
              }
              onClick={onSave}
            >
              {Remote.isLoading(companyUpdate.response) ? (
                <Loading.Component>Saving</Loading.Component>
              ) : (
                "Save"
              )}
            </Button>
          </Settings.Actions>
        }
        footerStyle={{
          backgroundColor: "#F2F5F7",
        }}
      >
        <Settings.Confirmation
          changedSettings={changedSettings}
          vehicleDefectTypeAddedCount={vehicleDefectTypeAddedCount}
          vehicleDefectTypeRemovedCount={vehicleDefectTypeRemovedCount}
          trailerDefectTypeAddedCount={trailerDefectTypeAddedCount}
          trailerDefectTypeRemovedCount={trailerDefectTypeRemovedCount}
          isOpen={isConfirmation}
          onCancel={() => {
            setConfirmation(false);
          }}
          onSave={onSave}
        ></Settings.Confirmation>
        <>
          <Layout.Block.Component bottom>
            <Alert
              showIcon
              icon={<Icon.Info />}
              type="info"
              message="New drivers are created using these default settings."
              style={{ fontSize: Typography.FontSize.data() }}
            />
          </Layout.Block.Component>
          <Form layout="vertical">
            <Form.Item
              name="address"
              label={<Settings.Label>Company Address</Settings.Label>}
            >
              <Company.Locations.SelectOne {...location} />
            </Form.Item>
            <Form.Item
              name="hoursOfServiceType"
              label={<Settings.Label>Hours of Service Rule</Settings.Label>}
            >
              <HoursOfService.Type.Select {...hoursOfServiceType} />
            </Form.Item>
            <Form.Item
              name="vehicleDefects"
              label={<Settings.Label>Vehicle Defect Tags</Settings.Label>}
            >
              {defectVehicleEdit}
            </Form.Item>
            <Form.Item
              name="trailerDefects"
              label={<Settings.Label>Trailer Defect Tags</Settings.Label>}
            >
              {defectTrailerEdit}
            </Form.Item>
            {
              <Form.Item
                name="dvirRequirements"
                label={
                  <>
                    <Settings.Label>DVIR Requirements</Settings.Label>
                    <Tooltip
                      title={
                        <>
                          <p style={{ borderBottom: "1px solid gray" }}>
                            <b>DVIR Requirement setting</b>
                          </p>
                          <p>
                            By default, photos and notes are <b>not</b> required
                            for any DVIRs. This includes DVIRs <b>with</b> and
                            <b>without</b> defects.
                          </p>
                        </>
                      }
                    >
                      <Icon.QuestionCircle
                        style={{ marginLeft: "8px" }}
                      ></Icon.QuestionCircle>
                    </Tooltip>
                  </>
                }
              >
                <Requirements.DVIREditor
                  settings={driveSettings}
                  loading={driveSettingsLoading}
                  onChange={setDriveSettings}
                />
              </Form.Item>
            }
            { enableElogsFacialRecognitionSuggestions && <Form.Item
                name="dvirRequirements"
                label={
                  <>
                    <Settings.Label>Facial Recognition Push Notifications</Settings.Label>
                    <Tooltip
                      title={
                        <>
                          <p style={{ borderBottom: "1px solid gray" }}>
                            <b>Pairing Assignment Suggestions</b>
                          </p>
                          <p>
                            By default, drivers do not receive push notifications for
                            asset pairing suggestions based on facial recognition from 
                            dash cameras. 
                          </p>
                        </>
                      }
                    >
                      <Icon.QuestionCircle
                        style={{ marginLeft: "8px" }}
                      ></Icon.QuestionCircle>
                    </Tooltip>
                  </>
                }
              >
                <Requirements.FacialRecognitionEditor
                  settings={driveSettings}
                  loading={driveSettingsLoading}
                  onChange={setDriveSettings}
                />
              </Form.Item>
            }
          </Form>
        </>

        {Remote.isError(companyUpdate.response) && (
          <Settings.Actions top>
            <ErrorMessage.Component danger icon>
              Something went wrong while updating the company settings!
            </ErrorMessage.Component>
          </Settings.Actions>
        )}
        {driveQueryApolloError &&
          Graph.Errors.renderApolloError(
            "Something went wrong fetching DVIR requirement settings.",
            driveQueryApolloError
          )}
        {driveMutationError &&
          Graph.Errors.renderApolloError(
            "Something went wrong saving DVIR requirement settings.",
            driveMutationError
          )}
      </Drawer>
    );
  }

  function DrawerHeader(): JSX.Element {
    return (
      <>
        <Typography.H1.Component style={{ fontWeight: "bold" }}>
          E-Logs Company Settings
        </Typography.H1.Component>
        <Typography.Component style={{ fontWeight: "normal", fontSize: 14 }}>
          These settings are company-wide and affect all branches.
        </Typography.Component>
      </>
    );
  }

  export function Confirmation({
    changedSettings,
    vehicleDefectTypeAddedCount,
    trailerDefectTypeAddedCount,
    vehicleDefectTypeRemovedCount,
    trailerDefectTypeRemovedCount,
    onSave,
    onCancel,
    isOpen,
  }: {
    readonly changedSettings: readonly SettingKeys[];
    readonly vehicleDefectTypeAddedCount: number;
    readonly vehicleDefectTypeRemovedCount: number;
    readonly trailerDefectTypeAddedCount: number;
    readonly trailerDefectTypeRemovedCount: number;
    readonly onSave: () => void;
    readonly onCancel: () => void;
    readonly isOpen: boolean;
  }) {
    const getDescriptionForSettingKey = (key: SettingKeys) => {
      const dataMap = {
        vehicleDefectTagsAdded: vehicleDefectTypeAddedCount.toString(),
        vehicleDefectTagsRemoved: vehicleDefectTypeRemovedCount.toString(),
        trailerDefectTagsAdded: trailerDefectTypeAddedCount.toString(),
        trailerDefectTagsRemoved: trailerDefectTypeRemovedCount.toString(),
      };
      const data: string =
        key in dataMap ? dataMap[key as keyof typeof dataMap] : "";
      return settingsConfirmChangedKeyDescriptions(data)[key];
    };

    const changedSettingsDescriptions = useMemo<
      readonly React.ReactNode[]
    >(() => {
      return changedSettings.map(getDescriptionForSettingKey);
    }, [changedSettings]);

    return (
      <Drawer
        title={<DrawerHeader />}
        placement="left"
        push={false}
        visible={isOpen}
        width={460}
        onClose={() => {
          onCancel();
        }}
        footer={
          <Settings.Actions>
            <Button onClick={onCancel}>Back</Button>
            <Button type="primary" onClick={onSave}>
              Save
            </Button>
          </Settings.Actions>
        }
        footerStyle={{
          backgroundColor: "#F2F5F7",
        }}
      >
        <Layout.Block.Component bottom>
          <Row gutter={[12, 24]}>
            <Col>
              <Alert
                showIcon
                icon={<Icon.ExclamationTriangle />}
                type="error"
                message="Accepting these changes will affect the entire company."
                style={{ fontSize: Typography.FontSize.data() }}
              />
            </Col>
            <Col>
              <Typography.H1.Component>
                Please confirm settings changes.
              </Typography.H1.Component>
            </Col>
            <Col>
              You are about to make{" "}
              {changedSettings.length > 1 ? "multiple " : ""}settings changes
              that will affect the <b>entire company</b>. These include:
            </Col>
            <Col>
              <List
                split={false}
                renderItem={(item) => <List.Item>● {item}</List.Item>}
                dataSource={[...changedSettingsDescriptions]}
              />
            </Col>
          </Row>
        </Layout.Block.Component>
      </Drawer>
    );
  }

  export const Label = Typography.H4.Component;
  export const Actions = styled(Layout.Block.Component)`
    display: flex;
    justify-content: space-between;

    ant-popover-arrow {
      display: hidden;
    }
  `;

  export const useRefresh = (initialValue = false) => {
    const [refresh, setRefresh] = React.useState({
      current: initialValue,
      last: initialValue,
    });

    return [
      refresh.current != refresh.last,
      (value: boolean) => setRefresh({ current: value, last: refresh.current }),
    ];
  };

  export namespace CancelWarn {
    export const warnOnCancel = (onComplete: (save: boolean) => void): void => {
      Modal.confirm({
        title: "You have not saved your changes",
        icon: (
          <DsIcon
            name="$icon_alert_line"
            color="$color_primary_red"
            size="$icon_size_5"
            layout={{
              margin: { right: "$spacing_2" },
            }}
          />
        ),
        content:
          "You are trying to exit without saving your Settings changes. Are you sure you want to leave without saving?",
        mask: false,
        okText: "Save and close",
        okButtonProps: {
          style: {
            borderRadius: "3px",
          },
        },
        cancelText: "Cancel changes",
        cancelButtonProps: {
          danger: false,
          ghost: false,
          style: {
            borderRadius: "3px",
          },
        },
        centered: false,
        className: "drawer-cancel-modal-wrapper",
        onOk() {
          onComplete(true);
        },
        onCancel() {
          onComplete(false);
        },
      });
    };
  }
}
